import { format } from "date-fns";
import { ru } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import type { NotificationItem } from "../../types";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface NotificationsTableProps {
  notifications: NotificationItem[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  handleResendNotification: (id: number) => Promise<void>;
}

const notificationStatusConfig: Record<
  NotificationItem["status"],
  {
    label: string;
    variant: "primary" | "success" | "info" | "warning" | "danger" | "muted";
  }
> = {
  partial: { label: "Частично", variant: "warning" },
  delivered: { label: "Доставлено", variant: "success" },
  failed: { label: "Ошибка", variant: "danger" },
};

export function NotificationsTable({
  notifications,
  page,
  pageSize,
  total,
  onPageChange,
  handleResendNotification,
}: NotificationsTableProps) {
  const navigate = useNavigate();

  const totalPages = Math.ceil(total / pageSize);

  const handleOpenDetails = (id: number) => {
    navigate(`/notifications/${id}`);
  };

  return (
    <div className="bg-white rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Заголовок</TableHead>
            <TableHead>Текст</TableHead>
            <TableHead>Получатели</TableHead>
            <TableHead>Действие</TableHead>
            <TableHead>Дата</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {notifications.map((item, index) => (
            <TableRow
              key={index} /* onClick={() => handleOpenDetails(item.id)} */
            >
              <TableCell
                className="font-medium"
                style={{
                  width: 250,
                  minWidth: 250,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {item.title}
              </TableCell>
              <TableCell
                style={{
                  width: 350,
                  minWidth: 350,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {item.message}
              </TableCell>
              <TableCell className="w-[50px] min-w-[50px]">
                {item.recipients}
              </TableCell>
              <TableCell>
                <Button
                  onClick={() => {
                    navigate(`/notifications/details`, {
                      state: {
                        titleCopy: item.title,
                        messageCopy: item.message,
                      },
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  Отправить снова
                </Button>
              </TableCell>
              <TableCell>
                {format(new Date(item.createdAt), "dd.MM.yyyy HH:mm", {
                  locale: ru,
                })}
              </TableCell>
              <TableCell>
                <Badge variant={notificationStatusConfig[item.status].variant}>
                  {notificationStatusConfig[item.status].label}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex justify-end items-center gap-2 mt-4">
        <span className="text-sm text-muted-foreground">
          {page} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
